<script setup>
import { onMounted } from 'vue';
import FullPageModal from '@/js/core/components/parts/FullPageModal.vue';
import RichTextEditor from '@/js/core/components/parts/editor/RichTextEditor.vue';
import FocalPointPicker from '@/js/core/components/parts/trays/FocalPointPicker.vue';
import TrayAccessFields from '@/js/core/components/parts/trays/TrayAccessFields.vue';
import TrayManagerAccessField from '@/js/core/components/parts/trays/TrayManagerAccessField.vue';
import { useFeaturedMediaUpload } from '@/js/core/composables/useFeaturedMediaUpload';
import { useModalClose } from '@/js/core/composables/useModalClose';
import { useProfileVisibility } from '@/js/core/composables/useProfileVisibility';
import { useCreateTray } from '@/js/core/composables/useCreateTray';

const PRIVATE_PROFILE_MESSAGE =
  'You are not able to create new Trays because your profile visibility is set to private. ' +
  'To change this, <a href="/profile">visit your profile</a> and change your profile visibility setting.';

const { isProfilePrivate, ensureProfileLoaded } = useProfileVisibility();

onMounted(ensureProfileLoaded);

const {
  imageUrl: imagePreviewUrl,
  focalPoint,
  fileName,
  alt: mediaAlt,
  caption: mediaCaption,
  isUploading,
  uploadError,
  handleFileChange,
  handleFileDrop,
  handleDragOver,
  removeImage: handleRemoveImage,
  cleanup: cleanupFeaturedMedia,
  createPayload,
} = useFeaturedMediaUpload();

const {
  form,
  isSubmitting,
  errorMessage,
  fieldErrors,
  editorRef,
  excludedManagerUuids,
  selectedManagerUsers,
  handleSubmit,
  handleMediaAltChange,
} = useCreateTray({ imageUrl: imagePreviewUrl, alt: mediaAlt, createPayload });

const { closeModal } = useModalClose({
  fallbackRoute: { name: 'trays' },
  cleanup: cleanupFeaturedMedia,
});

const handleAltChange = value => {
  mediaAlt.value = value;
  handleMediaAltChange(value);
};
</script>

<template>
  <FullPageModal title="Start New Tray" wrapper-class="is-tray-form-modal" @close="closeModal">
    <p v-if="isProfilePrivate" class="tray-form__visibility-message">
      <span v-html="PRIVATE_PROFILE_MESSAGE"></span>
    </p>
    <form v-else id="tray-form" class="tray-form" @submit.prevent="handleSubmit">
      <div v-if="errorMessage" class="tray-form__error" role="alert">
        {{ errorMessage }}
      </div>

      <div class="tray-form__grid">
        <div class="tray-form__grid--content__column">
          <div class="tray-form__group">
            <label for="tray-title" class="tray-form__label">
              Tray title<span class="tray-form__required">*</span>
            </label>
            <input
              id="tray-title"
              v-model="form.title"
              type="text"
              class="tray-form__input"
              :class="{ 'tray-form__input--error': fieldErrors.title }"
              required
              :aria-invalid="fieldErrors.title ? 'true' : undefined"
              :aria-describedby="fieldErrors.title ? 'tray-title-error' : undefined"
            />
            <p
              v-if="fieldErrors.title"
              id="tray-title-error"
              class="tray-form__field-error"
              role="alert"
            >
              {{ fieldErrors.title }}
            </p>
          </div>

          <div class="tray-form__group">
            <label id="create-featured-media-label" class="tray-form__label">Featured media</label>

            <template v-if="imagePreviewUrl">
              <FocalPointPicker
                v-model="focalPoint"
                v-model:caption="mediaCaption"
                :image-url="imagePreviewUrl"
                :file-name="fileName"
                :alt="mediaAlt"
                alt-required
                :alt-error="fieldErrors.featured_media_alt"
                id-prefix="create-featured"
                @update:alt="handleAltChange"
                @remove="handleRemoveImage"
              />
              <p v-if="isUploading" class="tray-form__hint">Uploading...</p>
            </template>

            <template v-else>
              <div class="tray-form__dropzone" @drop="handleFileDrop" @dragover="handleDragOver">
                <input
                  id="featured-media"
                  type="file"
                  class="tray-form__file-input"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  aria-labelledby="create-featured-media-label"
                  aria-describedby="create-featured-media-instructions create-featured-media-size-note"
                  @change="handleFileChange"
                />
                <label
                  id="create-featured-media-instructions"
                  for="featured-media"
                  class="tray-form__dropzone-label"
                >
                  Drop files here or click to browse
                </label>
              </div>
            </template>

            <p v-if="uploadError" class="tray-form__field-error">{{ uploadError }}</p>
            <p id="create-featured-media-size-note" class="tray-form__hint">
              The maximum allowed file size is 2MB.
            </p>
            <p class="tray-form__hint">
              This image will appear at the top of your Tray and as a Tray thumbnail. Please follow
              <a
                href="https://accessibility.huit.harvard.edu/images-and-media"
                target="_blank"
                rel="noopener noreferrer"
                >Harvard's image accessibility guidelines</a
              >.
            </p>
          </div>

          <div class="tray-form__group tray-form__group--full">
            <label id="create-tray-description-label" class="tray-form__label">
              Description<span class="tray-form__required">*</span>
            </label>
            <div class="tray-form__editor">
              <RichTextEditor
                ref="editorRef"
                editor-class="tray-form__textarea"
                toolbar-class="tray-form__toolbar"
                button-class="tray-form__toolbar-btn"
                aria-labelledby="create-tray-description-label"
                :required="true"
                :aria-invalid="!!fieldErrors.content"
                :aria-describedby="fieldErrors.content ? 'create-tray-description-error' : ''"
              />
            </div>
            <p
              v-if="fieldErrors.content"
              id="create-tray-description-error"
              class="tray-form__field-error"
              role="alert"
            >
              {{ fieldErrors.content }}
            </p>
            <p class="tray-form__hint">
              Briefly describe the purpose of your Tray. This will appear at the top of your Tray.
              First 20 words will be pulled into the Tray index.
            </p>
          </div>

          <p class="tray-form__required-note">* indicates required field</p>
        </div>
        <div class="tray-form__grid--content__column">
          <TrayAccessFields
            v-model:visible-to="form.visibleTo"
            v-model:comment-access="form.commentAccess"
            v-model:visible-users="form.visibleUsers"
            v-model:commentable-users="form.commentableUsers"
            id-prefix="create-tray"
            :role-users="selectedManagerUsers"
            :errors="fieldErrors"
            :show-required="true"
          >
            <template #after-visibility>
              <TrayManagerAccessField
                v-model:manager-access="form.managerAccess"
                v-model:manager-users="form.managerUsers"
                :excluded-uuids="excludedManagerUuids"
                :error="fieldErrors.manager_user_uuids"
                id-prefix="create-tray"
              />
            </template>
          </TrayAccessFields>
        </div>
      </div>
    </form>

    <template v-if="!isProfilePrivate" #footer>
      <button
        type="submit"
        form="tray-form"
        class="btn-primary tray-form__submit"
        :disabled="isSubmitting || isUploading"
      >
        {{ isSubmitting ? 'Creating...' : 'Create' }}
      </button>
      <button type="button" class="tray-form__cancel" :disabled="isSubmitting" @click="closeModal">
        Cancel
      </button>
    </template>
  </FullPageModal>
</template>
